import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ChartLine, TrendUp, TrendDown, CurrencyCircleDollar,
  ArrowsCounterClockwise, X, CalendarBlank, FilePdf, ArrowLeft, MapPin,
} from "@phosphor-icons/react";
import BudgetSitesLanding from "@/components/BudgetSitesLanding";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine,
} from "recharts";
import { SortableTh, useSortableRows } from "@/components/SortableTh";

const fmtTL = (n) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency", currency: "TRY", maximumFractionDigits: 0,
  }).format(Number(n) || 0);
const fmtTLDetail = (n) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency", currency: "TRY", maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const monthLabel = (period) => {
  // "2026-02" → "Şub 2026"
  const [y, m] = (period || "").split("-");
  const months = ["Oca", "Şub", "Mar", "Nis", "May", "Haz",
                  "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
  const i = parseInt(m, 10) - 1;
  return isNaN(i) ? period : `${months[i]} ${y}`;
};
const weekLabel = (period) => {
  // "2026-W07" → "2026 H7"
  return (period || "").replace("-W", " H");
};

const CustomTooltip = ({ active, payload, label, isWeek }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-slate-200 shadow-lg p-3 min-w-52 rounded-sm">
      <div className="text-xs font-bold text-slate-800 mb-2 border-b border-slate-100 pb-1.5">
        {isWeek ? weekLabel(label) : monthLabel(label)}
      </div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex justify-between items-center gap-4 py-0.5">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2" style={{ backgroundColor: p.color }} />
            <span className="text-[11px] text-slate-600">{p.name}:</span>
          </div>
          <span className="text-[11px] font-mono font-bold" style={{ color: p.color }}>
            {fmtTLDetail(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function BudgetCashFlow() {
  const { sites, activeSiteId, setActiveSiteId } = useSites();
  const [granularity, setGranularity] = useState("month"); // month | week
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState({ buckets: [], totals: {} });
  const [loading, setLoading] = useState(false);

  const currentSiteId = activeSiteId && activeSiteId !== "all" ? activeSiteId : "";
  const currentSite = sites.find((s) => s.id === currentSiteId);
  const showSiteLanding = !currentSiteId;

  const load = async () => {
    if (!currentSiteId) { setData({ buckets: [], totals: {} }); return; }
    setLoading(true);
    try {
      const params = { granularity };
      if (currentSiteId) params.site_id = currentSiteId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await axios.get(`${API}/budget/cashflow`, { params });
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [currentSiteId, granularity, dateFrom, dateTo]); // eslint-disable-line

  const chartData = useMemo(() => {
    const isWeek = granularity === "week";
    return (data.buckets || []).map((b) => ({
      ...b,
      label: isWeek ? weekLabel(b.period) : monthLabel(b.period),
    }));
  }, [data, granularity]);

  const totals = data.totals || { gelir: 0, gider: 0, net: 0 };
  const netSign = totals.net >= 0 ? "+" : "";
  const endingCash = chartData.length ? chartData[chartData.length - 1].cumulative_net : 0;

  const { sort, toggleSort, sortedRows: sortedChart } = useSortableRows(chartData, {
    defaultKey: "period", defaultDir: "asc",
    getters: {
      period: (r) => r.period || "",
      gelir: (r) => Number(r.gelir) || 0,
      gider: (r) => Number(r.gider) || 0,
      net: (r) => Number(r.net) || 0,
      cumulative_net: (r) => Number(r.cumulative_net) || 0,
      gelir_tahmin: (r) => Number(r.gelir_tahmin) || 0,
      gider_tahmin: (r) => Number(r.gider_tahmin) || 0,
    },
  });

  return (
    <Shell title="Nakit Akış Diyagramı" subtitle="Bütçe Yönetimi">
      {showSiteLanding ? (
        <BudgetSitesLanding
          title="Nakit Akış Diyagramı"
          subtitle="Nakit akış grafiğini görüntülemek için önce bir şantiye seçin."
          icon={ChartLine}
          accent="indigo"
          onSelectSite={(sid) => setActiveSiteId(sid)}
          testIdPrefix="cf"
        />
      ) : (
      <>
      {/* Şantiye Seçim Header */}
      <div className="flex items-center justify-between gap-4 p-3 mb-3 bg-[#F7FBE9] border border-[#95C11F] rounded-none sm:rounded-sm"
           data-testid="cf-site-scope-header">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => setActiveSiteId("all")}
                  className="p-1.5 rounded-full bg-white border border-[#95C11F] text-[#556B10] hover:bg-[#95C11F]/10 transition"
                  data-testid="cf-back-to-index">
            <ArrowLeft size={14} weight="bold" />
          </button>
          <MapPin size={18} weight="duotone" className="text-[#556B10] shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#556B10] font-bold">
              Aktif Şantiye
            </div>
            <div className="text-sm font-bold text-[#1B4776] truncate">
              <span className="font-mono">{currentSite?.code || currentSiteId}</span>
              {currentSite?.name && <span className="ml-2 text-slate-700">— {currentSite.name}</span>}
            </div>
          </div>
        </div>
        <button onClick={() => setActiveSiteId("all")}
                className="text-xs text-[#556B10] hover:underline shrink-0 font-semibold"
                data-testid="cf-change-site">
          Şantiye Değiştir
        </button>
      </div>

      {/* KPI Kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Toplam Gelir</div>
            <TrendUp size={16} weight="duotone" className="text-emerald-600" />
          </div>
          <div className="text-lg font-bold text-emerald-700 mt-1">{fmtTL(totals.gelir)}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-rose-700 font-bold">Toplam Gider</div>
            <TrendDown size={16} weight="duotone" className="text-rose-600" />
          </div>
          <div className="text-lg font-bold text-rose-700 mt-1">{fmtTL(totals.gider)}</div>
        </Card>
        <Card className={`p-4 rounded-none sm:rounded-sm border-slate-200 ${
          totals.net >= 0 ? "bg-[#F7FBE9] border-[#95C11F]" : "bg-rose-50 border-rose-300"
        }`}>
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-700">Net Nakit Akışı</div>
            <CurrencyCircleDollar size={16} weight="duotone" className={totals.net >= 0 ? "text-[#556B10]" : "text-rose-700"} />
          </div>
          <div className={`text-lg font-bold mt-1 ${totals.net >= 0 ? "text-[#556B10]" : "text-rose-700"}`}>
            {netSign}{fmtTL(totals.net)}
          </div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Kümülatif Bakiye</div>
            <ChartLine size={16} weight="duotone" className="text-[#1B4776]" />
          </div>
          <div className={`text-lg font-bold mt-1 ${endingCash >= 0 ? "text-[#1B4776]" : "text-rose-700"}`}>
            {fmtTL(endingCash)}
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">Dönem sonu</div>
        </Card>
      </div>

      {/* Kontrol Barı */}
      <Card className="p-4 mb-3 rounded-none sm:rounded-sm border-slate-200">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Granularity Toggle */}
          <div className="inline-flex border border-slate-300 rounded-none sm:rounded-sm overflow-hidden">
            <button
              onClick={() => setGranularity("month")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                granularity === "month"
                  ? "bg-[#1B4776] text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="cf-monthly"
            >
              Aylık
            </button>
            <button
              onClick={() => setGranularity("week")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors border-l border-slate-300 ${
                granularity === "week"
                  ? "bg-[#1B4776] text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="cf-weekly"
            >
              Haftalık
            </button>
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-1 ml-4">
            <CalendarBlank size={14} className="text-slate-400" />
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   className="rounded-none sm:rounded-sm w-40 h-8 text-xs" data-testid="cf-date-from" />
            <span className="text-slate-400 text-xs">—</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   className="rounded-none sm:rounded-sm w-40 h-8 text-xs" data-testid="cf-date-to" />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(""); setDateTo(""); }}
                      className="text-slate-400 hover:text-rose-600 p-1">
                <X size={12} weight="bold" />
              </button>
            )}
          </div>

          <Button variant="outline" onClick={load} disabled={loading}
                  className="ml-auto rounded-none sm:rounded-sm h-8 text-xs"
                  data-testid="cf-refresh">
            <ArrowsCounterClockwise size={12} weight="bold" className="mr-1" />
            Yenile
          </Button>
          <Button
            onClick={async () => {
              try {
                const params = new URLSearchParams({ granularity });
                if (currentSiteId) params.set("site_id", currentSiteId);
                if (dateFrom) params.set("date_from", dateFrom);
                if (dateTo) params.set("date_to", dateTo);
                const res = await axios.get(
                  `${API}/budget/cashflow.pdf?${params.toString()}`,
                  { responseType: "blob" },
                );
                const blob = new Blob([res.data], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `nakit_akis_${granularity}_${new Date().toISOString().slice(0,10)}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("PDF indirildi");
              } catch (e) { toast.error(formatErr(e)); }
            }}
            disabled={loading || chartData.length === 0}
            className="bg-[#B91C1C] hover:bg-[#991B1B] text-white rounded-none sm:rounded-sm h-8 text-xs"
            data-testid="cf-export-pdf">
            <FilePdf size={12} weight="bold" className="mr-1" />
            PDF İndir
          </Button>
        </div>
      </Card>

      {/* Grafik */}
      <Card className="p-4 mb-3 rounded-none sm:rounded-sm border-slate-200">
        {chartData.length === 0 ? (
          <div className="text-center text-slate-400 py-16">
            <ChartLine size={48} weight="duotone" className="mx-auto mb-3 text-slate-300" />
            <p className="text-sm">Bu döneme ait nakit akış kaydı yok.</p>
            <p className="text-xs text-slate-400 mt-1">Gelir/Gider kayıtları eklendiğinde burada zaman ekseninde görüntülenecek.</p>
          </div>
        ) : (
          <div style={{ width: "100%", height: 420 }} data-testid="cf-chart">
            <ResponsiveContainer>
              <ComposedChart data={chartData} margin={{ top: 20, right: 30, bottom: 40, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#475569" }}
                  angle={granularity === "week" ? -35 : 0}
                  textAnchor={granularity === "week" ? "end" : "middle"}
                  height={70}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#475569" }}
                  tickFormatter={(v) => {
                    if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
                    if (Math.abs(v) >= 1_000) return (v / 1_000).toFixed(0) + "K";
                    return v;
                  }}
                />
                <Tooltip content={<CustomTooltip isWeek={granularity === "week"} />} />
                <Legend
                  wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                  iconType="square"
                />
                <ReferenceLine y={0} stroke="#94a3b8" strokeWidth={1} />
                <Bar dataKey="gelir" name="Gelir (KDV Dahil)" fill="#10b981" barSize={granularity === "week" ? 12 : 22} />
                <Bar dataKey="gider" name="Gider (KDV Dahil)" fill="#f43f5e" barSize={granularity === "week" ? 12 : 22} />
                <Line
                  type="monotone"
                  dataKey="cumulative_net"
                  name="Kümülatif Bakiye"
                  stroke="#1B4776"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: "#1B4776" }}
                  activeDot={{ r: 6 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Bucket Tablosu */}
      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
            Dönem Detayı ({granularity === "month" ? "Aylık" : "Haftalık"})
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-slate-50">
                <SortableTh label="Dönem" k="period" sort={sort} onSort={toggleSort} className="px-4 py-2.5 text-left w-32" />
                <SortableTh label="Gelir" k="gelir" sort={sort} onSort={toggleSort} className="px-4 py-2.5 text-right w-32" align="right" />
                <SortableTh label="Gider" k="gider" sort={sort} onSort={toggleSort} className="px-4 py-2.5 text-right w-32" align="right" />
                <SortableTh label="Net" k="net" sort={sort} onSort={toggleSort} className="px-4 py-2.5 text-right w-32" align="right" />
                <SortableTh label="Kümülatif" k="cumulative_net" sort={sort} onSort={toggleSort} className="px-4 py-2.5 text-right w-32" align="right" />
                <SortableTh label="Tahmin Gelir" k="gelir_tahmin" sort={sort} onSort={toggleSort} className="px-4 py-2.5 text-right w-32" align="right" />
                <SortableTh label="Tahmin Gider" k="gider_tahmin" sort={sort} onSort={toggleSort} className="px-4 py-2.5 text-right w-32" align="right" />
              </tr>
            </thead>
            <tbody data-testid="cf-table-body">
              {sortedChart.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-400 text-xs">
                    Veri yok
                  </td>
                </tr>
              )}
              {sortedChart.map((b, i) => (
                <tr key={b.period} className={`border-b border-slate-100 hover:bg-slate-50 ${i % 2 ? "bg-slate-50/50" : ""}`}>
                  <td className="px-4 py-2 font-mono text-xs text-slate-700 font-bold">{b.label}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-emerald-700">{fmtTL(b.gelir)}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-rose-700">{fmtTL(b.gider)}</td>
                  <td className={`px-4 py-2 text-right font-mono text-xs font-bold ${
                    b.net >= 0 ? "text-emerald-800" : "text-rose-800"
                  }`}>{b.net >= 0 ? "+" : ""}{fmtTL(b.net)}</td>
                  <td className={`px-4 py-2 text-right font-mono text-xs font-bold ${
                    b.cumulative_net >= 0 ? "text-[#1B4776]" : "text-rose-800"
                  }`}>{fmtTL(b.cumulative_net)}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-slate-400">{fmtTL(b.gelir_tahmin)}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-slate-400">{fmtTL(b.gider_tahmin)}</td>
                </tr>
              ))}
              {chartData.length > 0 && (
                <tr className="bg-slate-100 border-t-2 border-slate-300">
                  <td className="px-4 py-2.5 font-mono text-xs font-bold text-slate-800">TOPLAM</td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs font-bold text-emerald-800">{fmtTL(totals.gelir)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs font-bold text-rose-800">{fmtTL(totals.gider)}</td>
                  <td className={`px-4 py-2.5 text-right font-mono text-sm font-bold ${
                    totals.net >= 0 ? "text-emerald-800" : "text-rose-800"
                  }`}>{netSign}{fmtTL(totals.net)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs font-bold text-[#1B4776]">{fmtTL(endingCash)}</td>
                  <td className="px-4 py-2.5" />
                  <td className="px-4 py-2.5" />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      </>
      )}
    </Shell>
  );
}
